import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Facebook, Instagram, Youtube } from "lucide-react";
import { SITE, NAV_LINKS, fullAddress } from "@/config/site";
import { SERVICES } from "@/data/services";
import { telLink, waLink, WA_MESSAGES } from "@/utils/whatsapp";
import { Logo } from "@/components/Logo";

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.1-1.3A10 10 0 1 0 12 2zm5.5 14.2c-.2.7-1.3 1.3-1.9 1.3-.5.1-1.1.2-3.3-.7-2.8-1.1-4.6-4-4.7-4.2-.1-.2-1.1-1.5-1.1-2.9s.7-2 1-2.3c.2-.3.5-.3.7-.3h.5c.2 0 .4 0 .6.4l.9 2.1c.1.2.1.4 0 .6l-.4.6-.5.5c-.2.2-.3.4-.1.7.2.3.9 1.5 2 2.4 1.4 1.2 2.5 1.6 2.9 1.7.3.2.5.1.7-.1l1-1.2c.2-.3.5-.2.8-.1l2 1c.3.1.5.2.6.4 0 .1 0 .7-.2 1.3z" />
  </svg>
);

export { WhatsAppIcon };

export const Footer = () => (
  <footer className="bg-navy-950 text-slate-300" data-testid="site-footer">
    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8">
      <div>
        <Logo light />
        <p className="mt-4 text-sm leading-relaxed text-slate-400">
          {SITE.tagline} Quality medical care for Kasdol and nearby communities.
        </p>
        <div className="mt-5 flex gap-3">
          {[
            { icon: Facebook, label: "Facebook", href: SITE.socials.facebook || "#" },
            { icon: Instagram, label: "Instagram", href: SITE.socials.instagram || "#" },
            { icon: Youtube, label: "YouTube", href: SITE.socials.youtube || "#" },
          ].map(({ icon: I, label, href }) => (
            <a
              key={label}
              href={href}
              aria-label={`${label} (link to be updated)`}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-slate-300 transition-colors hover:bg-emerald-600 hover:text-white"
              data-testid={`footer-social-${label.toLowerCase()}`}
            >
              <I className="h-4 w-4" aria-hidden="true" />
            </a>
          ))}
        </div>
      </div>

      <nav aria-label="Footer">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-white">Quick Links</h3>
        <ul className="mt-4 space-y-2.5 text-sm">
          {NAV_LINKS.map((l) => (
            <li key={l.path}>
              <Link to={l.path} className="transition-colors hover:text-emerald-400" data-testid={`footer-link-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.name}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/health-tips" className="transition-colors hover:text-emerald-400" data-testid="footer-link-health-tips">Health Tips</Link>
          </li>
        </ul>
      </nav>

      <div>
        <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-white">Our Services</h3>
        <ul className="mt-4 space-y-2.5 text-sm">
          {SERVICES.slice(0, 6).map((s) => (
            <li key={s.slug}>
              <Link to={`/services/${s.slug}`} className="transition-colors hover:text-emerald-400" data-testid={`footer-service-${s.slug}`}>
                {s.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="font-heading text-sm font-semibold uppercase tracking-widest text-white">Contact</h3>
        <ul className="mt-4 space-y-3 text-sm">
          <li>
            <a href={telLink} className="flex items-center gap-2.5 transition-colors hover:text-emerald-400" data-testid="footer-phone-link">
              <Phone className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" /> {SITE.phoneDisplay}
            </a>
          </li>
          <li>
            <a href={waLink(WA_MESSAGES.general)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-emerald-400" data-testid="footer-whatsapp-link">
              <WhatsAppIcon className="h-4 w-4 shrink-0 text-emerald-400" /> WhatsApp {SITE.phoneDisplay}
            </a>
          </li>
          <li className="flex items-start gap-2.5">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" /> {fullAddress()}
          </li>
          {SITE.hours.map((h) => (
            <li key={h.label} className="flex items-start gap-2.5">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
              <span>{h.label}: {h.value}{h.isDemo && <span className="text-slate-500"> (demo)</span>}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-slate-500 sm:flex-row sm:px-6 lg:px-8">
        <p>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
        <div className="flex items-center gap-4">
          <Link to="/privacy-policy" className="transition-colors hover:text-emerald-400" data-testid="footer-link-privacy">Privacy Policy</Link>
          <span>Some content shown is demo placeholder pending final hospital details.</span>
        </div>
      </div>
    </div>
  </footer>
);
